<script lang="ts">
  import type { RoomState } from '../room/state.svelte';
  import { identitySuffix } from '../session/username';
  import { CHAT_MESSAGE_MAX_BYTES, checkChatText, formatChatAge } from './chat';

  const { room }: { room: RoomState } = $props();

  let draft = $state('');
  /** Local validation message; a server rejection arrives as a room notice. */
  let draftError = $state<string | null>(null);
  let list = $state<HTMLUListElement | null>(null);
  /**
   * Whether the list was scrolled to the bottom before the last append. New
   * messages only pull the view down when the reader is already there, so
   * scrolling back through history is not yanked away by someone else typing.
   */
  let atBottom = $state(true);

  const messages = $derived(room.social.messages);
  const you = $derived(room.you);
  const checked = $derived(checkChatText(draft));
  const canSend = $derived(room.live && checked.ok && !room.isBusy('send-chat'));
  /**
   * Estimated server clock for relative ages. Re-read whenever a new clock
   * sample lands (every 5 s in steady state), which is frequent enough for
   * minute-granularity ages, and `null` before the first estimate.
   */
  const serverNowMs = $derived.by(() => {
    void room.clock;
    return room.serverNowMs();
  });

  $effect(() => {
    // Depend on the log so this runs after every append.
    void messages.length;
    if (list === null || !atBottom) return;
    list.scrollTop = list.scrollHeight;
  });

  function trackScroll(event: Event): void {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    atBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 24;
  }

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const result = await room.social.sendChat(draft);
    if (result.kind === 'refused') {
      // Keep the text so it can be fixed instead of retyped.
      draftError = result.message;
      return;
    }
    draftError = null;
    // Cleared on acceptance only. The message itself appears when the
    // server's broadcast arrives — there is no optimistic echo.
    if (result.outcome.result === 'ok') draft = '';
  }
</script>

<section class="chat" aria-labelledby="chat-heading">
  <h2 id="chat-heading">Chat</h2>

  <ul bind:this={list} onscroll={trackScroll} role="log" aria-live="polite" aria-relevant="additions">
    {#each messages as message (message.id)}
      <li class:mine={message.identity === you?.identity}>
        <p class="meta">
          <span class="who">{message.username}</span>
          {#if message.identity === you?.identity}
            <span class="badge">you</span>
          {:else}
            <span class="suffix">#{identitySuffix(message.identity)}</span>
          {/if}
          <span class="age"
            >{formatChatAge(
              serverNowMs === null ? null : Math.max(0, serverNowMs - message.sent_server_ms),
            )}</span
          >
        </p>
        <!-- Text interpolation only: a message that looks like markup renders
             as its characters. Never `{@html}` here. -->
        <p class="text">{message.text}</p>
      </li>
    {/each}
  </ul>
  {#if messages.length === 0}
    <p class="empty">No messages yet. Chat is cleared when the room resets.</p>
  {/if}

  <form onsubmit={submit}>
    <div class="field">
      <label class="visually-hidden" for="chat-input">Message</label>
      <input
        id="chat-input"
        type="text"
        autocomplete="off"
        placeholder={room.live ? 'Say something…' : 'Reconnecting…'}
        disabled={!room.live}
        bind:value={draft}
        aria-describedby="chat-bound"
        aria-invalid={draftError !== null}
      />
    </div>
    <button type="submit" class="primary" disabled={!canSend}>Send</button>
    <p id="chat-bound" class="hint" class:over={checked.bytes > CHAT_MESSAGE_MAX_BYTES}>
      {#if checked.bytes > CHAT_MESSAGE_MAX_BYTES * 0.75}
        {checked.bytes} / {CHAT_MESSAGE_MAX_BYTES} bytes
      {:else}
        Plain text, up to {CHAT_MESSAGE_MAX_BYTES} bytes. Not saved anywhere.
      {/if}
    </p>
    {#if draftError !== null}
      <p class="alert" role="alert">{draftError}</p>
    {/if}
  </form>
</section>

<style>
  .chat {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 0;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0.1rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    /* Bounded height: chat scrolls inside its pane instead of pushing the
       participant list or settings off screen. */
    max-height: 18rem;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  li {
    min-width: 0;
  }

  .meta {
    margin: 0;
    display: flex;
    align-items: baseline;
    gap: 0.35rem;
    flex-wrap: wrap;
    font-size: 0.78rem;
    color: var(--wa-text-dim);
  }

  .who {
    font-weight: 600;
    color: var(--wa-text);
    overflow-wrap: anywhere;
  }

  li.mine .who {
    color: var(--wa-accent);
  }

  .suffix {
    font-variant-numeric: tabular-nums;
  }

  .badge {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border: 1px solid var(--wa-border);
    border-radius: 0.25rem;
    padding: 0 0.3rem;
  }

  .text {
    margin: 0;
    /* A 2 KiB single "word" must wrap instead of widening the pane. */
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }

  .empty {
    margin: 0;
    color: var(--wa-text-dim);
    font-size: 0.9rem;
  }

  form {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .field {
    flex: 1 1 8rem;
    min-width: 0;
    display: flex;
  }

  .field input {
    width: 100%;
  }

  .hint {
    flex-basis: 100%;
    margin: 0;
    font-variant-numeric: tabular-nums;
  }

  .hint.over {
    color: var(--wa-danger);
  }

  .alert {
    flex-basis: 100%;
    margin: 0;
    font-size: 0.85rem;
    color: var(--wa-danger);
  }
</style>
